import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Trophy, Calendar, TrendingUp, Sparkles, Loader2, CheckCircle2, CreditCard,
  Pencil, RotateCcw, MoreHorizontal, Lock, CalendarClock,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Milestones as MilestonesApi, Cards as CardsApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatINR, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Project when a milestone will hit at a given monthly run-rate. Falls back to
// the empirical user-supplied monthly spend on the card; defaults to ₹60k/mo
// if we don't have that info yet.
const projectedDate = (current, target, monthlySpend = 60000) => {
  if (current >= target) return 'Achieved';
  const remaining = target - current;
  if (monthlySpend <= 0) return '—';
  const months = Math.ceil(remaining / monthlySpend);
  const dt = new Date();
  dt.setMonth(dt.getMonth() + months);
  return formatDate(dt);
};

// Group a flat milestones list by card.
const groupByCard = (items) => {
  const map = new Map();
  for (const m of items || []) {
    const key = m.card_id || `${m.bank}__${m.card_name}`;
    if (!map.has(key)) {
      map.set(key, {
        cardId: m.card_id,
        cardName: m.card_name,
        bank: m.bank,
        milestones: [],
      });
    }
    map.get(key).milestones.push(m);
  }
  for (const g of map.values()) {
    g.milestones.sort((a, b) => {
      const pa = (a.current_inr || 0) / Math.max(1, a.target_inr || 1);
      const pb = (b.current_inr || 0) / Math.max(1, b.target_inr || 1);
      if (Math.abs(pa - pb) < 0.0001) return (a.target_inr || 0) - (b.target_inr || 0);
      return pb - pa;
    });
  }
  return Array.from(map.values()).sort((a, b) => {
    const av = a.milestones.reduce((s, m) => s + (m.achieved ? 0 : (m.reward_value_inr || 0)), 0);
    const bv = b.milestones.reduce((s, m) => s + (m.achieved ? 0 : (m.reward_value_inr || 0)), 0);
    return bv - av;
  });
};

// Single source of truth for milestone-state mutations. All paths route to
// PATCH /api/cards/me/{card_id} so the engine keeps milestone_progress and
// achieved_milestone_ids consistent.
const useMilestoneMutations = (onChanged) => {
  const [saving, setSaving] = useState(false);
  const markAchieved = async (m, achieved) => {
    setSaving(true);
    try {
      // The PATCH endpoint REPLACES achieved_milestone_ids when the caller
      // supplies it. We need to send the full *new* set, so we have to read
      // the existing list first via /cards/me.
      const my = await CardsApi.mine();
      const card = (my || []).find((c) => c.id === m.card_id);
      const current = new Set(card?.achieved_milestone_ids || []);
      if (achieved) current.add(m.milestone_id);
      else current.delete(m.milestone_id);
      await CardsApi.update(m.card_id, {
        achieved_milestone_ids: Array.from(current),
      });
      toast.success(achieved ? 'Marked as achieved' : 'Marked as not achieved');
      onChanged?.();
    } catch (e) {
      toast.error(e?.message || 'Could not update milestone');
    } finally {
      setSaving(false);
    }
  };
  const editProgress = async (m, newInr) => {
    setSaving(true);
    try {
      await CardsApi.update(m.card_id, {
        milestone_progress: { [m.milestone_id]: Number(newInr) || 0 },
      });
      toast.success('Progress updated');
      onChanged?.();
    } catch (e) {
      toast.error(e?.message || 'Could not update progress');
    } finally {
      setSaving(false);
    }
  };
  const resetProgress = async (m) => {
    setSaving(true);
    try {
      // Two things to do: clear achieved flag if set + zero progress.
      const my = await CardsApi.mine();
      const card = (my || []).find((c) => c.id === m.card_id);
      const current = new Set(card?.achieved_milestone_ids || []);
      current.delete(m.milestone_id);
      await CardsApi.update(m.card_id, {
        achieved_milestone_ids: Array.from(current),
        milestone_progress: { [m.milestone_id]: 0 },
      });
      toast.success('Milestone reset');
      onChanged?.();
    } catch (e) {
      toast.error(e?.message || 'Could not reset milestone');
    } finally {
      setSaving(false);
    }
  };
  return { saving, markAchieved, editProgress, resetProgress };
};

const EditProgressDialog = ({ milestone, open, onOpenChange, onSubmit, saving }) => {
  const [val, setVal] = useState('');
  useEffect(() => {
    if (milestone) setVal(String(milestone.current_inr || 0));
  }, [milestone]);
  if (!milestone) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="milestone-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit progress · {milestone.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Current spend toward this milestone (₹)</Label>
          <Input
            inputMode="numeric"
            value={val}
            onChange={(e) => setVal(e.target.value.replace(/[^0-9.]/g, ''))}
            className="h-10 font-mono-num"
            data-testid="milestone-edit-input"
          />
          <div className="text-[10px] text-muted-foreground">
            Target {formatINR(milestone.target_inr, { compact: true })}. Edit cumulative spend so far — engine uses this to decide whether the next transaction crosses the threshold.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => onSubmit(milestone, val)}
            disabled={saving}
            data-testid="milestone-edit-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Phase 14 — modal that lets the user confirm a card's joining date from
// anywhere a locked milestone surfaces. Confirming the date unlocks BOTH
// anniversary milestones AND welcome benefits for that card by flipping
// the backend's `joining_date_is_estimate` flag to false.
const SetJoiningDateDialog = ({ card, open, onOpenChange, onSaved }) => {
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(''); }, [card?.cardId]);
  if (!card) return null;
  const submit = async () => {
    if (!val) { toast.error('Pick a joining date first.'); return; }
    setSaving(true);
    try {
      // Backend automatically flips joining_date_is_estimate to false when
      // a valid approved_at is supplied via PATCH.
      await CardsApi.update(card.cardId, { approved_at: val });
      toast.success(`${card.cardName} — joining date confirmed. Milestones unlocked.`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || 'Could not save joining date');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="milestone-set-joining-date-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-accent" />
            Set joining date · {card.cardName}
          </DialogTitle>
          <DialogDescription>
            We use this to anchor anniversary milestones and welcome benefits. Approximate
            month is OK — pick the 1st of that month if you don&rsquo;t remember the exact day.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Card approval / joining date</Label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="h-10 pl-10"
              data-testid="milestone-set-joining-date-input"
            />
          </div>
          <div className="text-[10px] text-muted-foreground">
            Confirming this date will unlock all anniversary and welcome milestones for this card
            and start tracking spend from the correct anchor.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !val}
            data-testid="milestone-set-joining-date-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Confirm date
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Inline milestone row — shows progress + action menu.
// Phase 14 — when `m.locked` is true (anniversary/welcome milestone with no
// confirmed joining date), the row collapses to a "locked" presentation:
// it hides the progress bar and the actions menu, and shows a clear
// explanation + a "Set joining date" CTA that opens the dialog.
const MilestoneRow = ({ m, onEdit, mutations, onSetJoiningDate }) => {
  const target = m.target_inr || 0;
  const current = m.current_inr || 0;
  const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  const remaining = Math.max(0, target - current);
  const achieved = !!m.achieved || pct >= 100;
  const locked = !!m.locked;

  if (locked) {
    const isWelcome = m.lock_copy_key === 'welcome' || m.is_welcome;
    return (
      <div
        className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/[0.04] px-3 py-2.5 space-y-2"
        data-testid={`milestone-row-${m.id}`}
        data-locked="true"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-sm font-medium truncate text-foreground/80">
              <Lock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span className="truncate">{m.name}</span>
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Sparkles className="h-3 w-3 text-accent shrink-0" />
              <span className="truncate">{m.reward}</span>
              <Badge variant="outline" className="text-[9px] h-4 px-1.5 uppercase tracking-wider text-amber-600 dark:text-amber-400 border-amber-500/30 bg-amber-500/5">
                {isWelcome ? 'Welcome' : m.period || 'Anniversary'}
              </Badge>
            </div>
          </div>
          <Badge
            variant="outline"
            className="font-mono-num text-[10px] text-amber-600 dark:text-amber-400 border-amber-500/30 bg-amber-500/5 shrink-0"
          >
            Locked
          </Badge>
        </div>
        <div className="text-[11px] text-amber-700 dark:text-amber-300/90 leading-snug">
          {isWelcome
            ? 'Welcome benefits depend on your card joining date. Set it to start tracking the spend window.'
            : 'Anniversary milestones reset on your card joining date. Set it to start tracking accurately.'}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1.5 border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-300"
          onClick={() => onSetJoiningDate?.(m)}
          data-testid={`milestone-set-joining-date-${m.id}`}
        >
          <CalendarClock className="h-3 w-3" /> Set joining date
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2.5 space-y-2',
        achieved
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : pct >= 70
            ? 'border-accent/30 bg-accent/5'
            : 'border-border bg-[hsl(var(--surface-2))]'
      )}
      data-testid={`milestone-row-${m.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-medium truncate">
            {achieved && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
            <span className="truncate">{m.name}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Sparkles className="h-3 w-3 text-accent shrink-0" />
            <span className="truncate">{m.reward}</span>
            {m.period && (
              <Badge variant="outline" className="text-[9px] h-4 px-1.5 uppercase tracking-wider">
                {m.period}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Badge
            variant={achieved ? 'default' : pct >= 70 ? 'secondary' : 'outline'}
            className={cn(
              'font-mono-num text-[10px]',
              achieved && 'bg-emerald-500 hover:bg-emerald-600'
            )}
            data-testid={`milestone-row-progress-${m.id}`}
          >
            {achieved ? 'Done' : `${pct}%`}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-full"
                aria-label="Milestone actions"
                data-testid={`milestone-actions-${m.id}`}
              >
                <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); mutations.markAchieved(m, !achieved); }}
                className="cursor-pointer"
                data-testid={`milestone-mark-${m.id}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-2" />
                {achieved ? 'Mark as not achieved' : 'Mark as achieved'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); onEdit(m); }}
                className="cursor-pointer"
                data-testid={`milestone-edit-${m.id}`}
              >
                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit progress
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); mutations.resetProgress(m); }}
                className="cursor-pointer text-rose-500 focus:text-rose-500"
                data-testid={`milestone-reset-${m.id}`}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-2" /> Reset
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {!achieved && (
        <>
          <Progress value={pct} className="h-1" />
          <div className="flex items-center justify-between text-[10px] text-muted-foreground font-mono-num">
            <span>{formatINR(current, { compact: true })} / {formatINR(target, { compact: true })}</span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {projectedDate(current, target)}
            </span>
            <span>{formatINR(remaining, { compact: true })} to go</span>
          </div>
        </>
      )}
      {achieved && (
        <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono-num">
          {formatINR(m.reward_value_inr || 0, { compact: true })} credited
        </div>
      )}
    </div>
  );
};

export default function MilestonesPage() {
  const { user, demoLogin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMs, setEditingMs] = useState(null);
  // Phase 14 — when the user clicks "Set joining date" on a locked
  // milestone (or the per-card group banner), we open this dialog with a
  // tiny `{ cardId, cardName }` payload. Confirming the date PATCHes the
  // user_card on the backend which unlocks every locked milestone in
  // that card group on the next load.
  const [joiningDateCard, setJoiningDateCard] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      if (!user) await demoLogin();
      const data = await MilestonesApi.mine();
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Phase 17 — milestone progress depends on cards + transactions, so
  // any mutation elsewhere (e.g. editing a Gmail-imported txn to a
  // different card) should refresh the list.
  useEffect(() => {
    const handler = (e) => {
      const ent = e?.detail?.entity;
      if (ent === 'card' || ent === 'transaction') load();
    };
    window.addEventListener('rb:data-changed', handler);
    return () => window.removeEventListener('rb:data-changed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutations = useMilestoneMutations(load);

  const submitEdit = async (m, newInr) => {
    await mutations.editProgress(m, newInr);
    setEditingMs(null);
  };

  // Open the Set Joining Date dialog for the card that owns this milestone.
  const handleSetJoiningDateFromMilestone = (m) => {
    setJoiningDateCard({ cardId: m.card_id, cardName: m.card_name });
  };

  const groups = useMemo(() => groupByCard(items), [items]);
  const totalCards = groups.length;
  const activeMilestones = items.filter((m) => !m.achieved).length;
  const achievedMilestones = items.filter((m) => m.achieved).length;
  const totalRewardsToUnlock = items
    .filter((m) => !m.achieved)
    .reduce((s, m) => s + (m.reward_value_inr || 0), 0);

  if (loading) {
    return (
      <div className="min-h-[40vh] grid place-items-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="milestones-page">
      {/* Header */}
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-accent font-medium">Goals</div>
        <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Trophy className="h-7 w-7 text-accent" /> Milestone Tracker
        </h1>
        <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
          Card-wise milestone progress, what&rsquo;s already credited, and projected hit dates. Use the
          per-milestone actions to override progress, mark something as already achieved, or reset
          it to a clean slate.
        </p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="milestones-summary-cards">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Cards with milestones</div>
          <div className="mt-1 font-display text-2xl font-semibold flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-muted-foreground" /> {totalCards}
          </div>
        </Card>
        <Card className="p-4" data-testid="milestones-summary-active">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Active milestones</div>
          <div className="mt-1 font-display text-2xl font-semibold">{activeMilestones}</div>
        </Card>
        <Card className="p-4" data-testid="milestones-summary-achieved">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Already achieved</div>
          <div className="mt-1 font-display text-2xl font-semibold flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-5 w-5" /> {achievedMilestones}
          </div>
        </Card>
        <Card className="p-4" data-testid="milestones-summary-unlock">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Rewards to unlock</div>
          <div className="mt-1 font-display text-2xl font-semibold">{formatINR(totalRewardsToUnlock, { compact: true })}</div>
          <div className="mt-1 text-[10px] text-emerald-500 inline-flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> Estimated value
          </div>
        </Card>
      </div>

      {/* Card-wise groups */}
      {groups.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <Trophy className="h-8 w-8 mx-auto text-muted-foreground opacity-60" />
          <div className="mt-3 text-sm font-medium">No milestones yet</div>
          <div className="mt-1 text-xs text-muted-foreground max-w-md mx-auto">
            Add a card from My Cards. Milestones for each card will show up here as you spend.
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g, i) => {
            const remainingValue = g.milestones
              .filter((m) => !m.achieved)
              .reduce((s, m) => s + (m.reward_value_inr || 0), 0);
            const achievedCount = g.milestones.filter((m) => m.achieved).length;
            // Phase 14 — if the entire group is locked because the user has
            // no confirmed joining date, surface a single prominent banner at
            // the top of the group instead of repeating the CTA on every row.
            const lockedCount = g.milestones.filter((m) => m.locked).length;
            const allLocked = lockedCount > 0 && lockedCount === g.milestones.length;
            return (
              <motion.div
                key={g.cardId || g.cardName}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <Card className="p-4" data-testid={`milestones-card-${g.cardId || 'unknown'}`}>
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{g.bank}</div>
                      <div className="font-display text-base sm:text-lg font-semibold truncate">{g.cardName}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {lockedCount > 0 && (
                        <Badge variant="outline" className="text-[10px] gap-1 text-amber-600 dark:text-amber-400 border-amber-500/30 bg-amber-500/5" data-testid={`milestones-group-locked-${g.cardId}`}>
                          <Lock className="h-3 w-3" /> {lockedCount} locked
                        </Badge>
                      )}
                      {achievedCount > 0 && (
                        <Badge variant="outline" className="text-[10px] gap-1 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/5">
                          <CheckCircle2 className="h-3 w-3" /> {achievedCount} done
                        </Badge>
                      )}
                      {remainingValue > 0 && (
                        <Badge variant="outline" className="text-[10px] gap-1">
                          <Sparkles className="h-3 w-3 text-accent" />
                          {formatINR(remainingValue, { compact: true })} to unlock
                        </Badge>
                      )}
                    </div>
                  </div>
                  {allLocked && (
                    <div
                      className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row"
                      data-testid={`milestones-group-banner-${g.cardId}`}
                    >
                      <div className="flex items-start gap-2 text-[12px] leading-snug">
                        <Lock className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                        <span className="text-amber-700 dark:text-amber-300/90">
                          <span className="font-medium">Confirm your joining date</span> to unlock anniversary and welcome milestones for this card. We&rsquo;ll start tracking from the correct anchor.
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1.5 border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 shrink-0"
                        onClick={() => setJoiningDateCard({ cardId: g.cardId, cardName: g.cardName })}
                        data-testid={`milestones-group-set-date-${g.cardId}`}
                      >
                        <CalendarClock className="h-3.5 w-3.5" /> Set joining date
                      </Button>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {g.milestones.map((m) => (
                      <MilestoneRow
                        key={m.id}
                        m={m}
                        onEdit={(ms) => setEditingMs(ms)}
                        mutations={mutations}
                        onSetJoiningDate={handleSetJoiningDateFromMilestone}
                      />
                    ))}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      <EditProgressDialog
        milestone={editingMs}
        open={!!editingMs}
        onOpenChange={(v) => { if (!v) setEditingMs(null); }}
        onSubmit={submitEdit}
        saving={mutations.saving}
      />
      <SetJoiningDateDialog
        card={joiningDateCard}
        open={!!joiningDateCard}
        onOpenChange={(v) => { if (!v) setJoiningDateCard(null); }}
        onSaved={load}
      />
    </div>
  );
}
